import { useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EnvelopeSimple, WhatsappLogo, Eye, PaperPlaneTilt, Clock, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

const CHANNEL_META = {
  email:    { label: "E-Mail",   icon: EnvelopeSimple, accent: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  whatsapp: { label: "WhatsApp", icon: WhatsappLogo,   accent: "bg-green-100 text-green-800 border-green-200" },
};

export default function Messages() {
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages]   = useState([]);
  const [bookings, setBookings]   = useState([]);
  const [previewFor, setPreviewFor] = useState(null); // {template, lang}
  const [previewData, setPreviewData] = useState(null);
  const [previewBookingId, setPreviewBookingId] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendFor, setSendFor] = useState(null); // template
  const [sendBookingId, setSendBookingId] = useState("");
  const [sendBusy, setSendBusy] = useState(false);

  const loadAll = () => {
    api.get("/templates/catalog").then(r => setTemplates(r.data?.templates || []));
    api.get("/messages").then(r => setMessages(r.data || [])).catch(()=>{});
    api.get("/bookings").then(r => setBookings(r.data || [])).catch(()=>{});
  };
  useEffect(() => { loadAll(); }, []);

  const openPreview = (t, lang) => {
    setPreviewFor({ template: t, lang });
    setPreviewData(null);
    setPreviewBookingId(bookings[0]?.id || "");
  };

  useEffect(() => {
    if (!previewFor) return;
    const { template, lang } = previewFor;
    // WhatsApp braucht kein booking, Email schon (aber mit Fallback zu Beispiel)
    if (template.channel === "email" && !previewBookingId) { setPreviewData(null); return; }
    setPreviewLoading(true);
    const params = new URLSearchParams({ key: template.key, channel: template.channel, lang });
    if (previewBookingId) params.set("booking_id", previewBookingId);
    api.get(`/templates/preview?${params.toString()}`)
      .then(r => setPreviewData(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); setPreviewData(null); })
      .finally(() => setPreviewLoading(false));
  }, [previewFor, previewBookingId]);

  const sendNow = async () => {
    if (!sendFor || !sendBookingId) { toast.error("Bitte Buchung wählen"); return; }
    setSendBusy(true);
    try {
      await api.post("/templates/send", {
        key: sendFor.key, channel: sendFor.channel, booking_id: sendBookingId,
      });
      toast.success(`${CHANNEL_META[sendFor.channel].label} versendet`);
      setSendFor(null); setSendBookingId("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally { setSendBusy(false); }
  };

  const emailTemplates = useMemo(() => templates.filter(t => t.channel === "email"), [templates]);
  const waTemplates    = useMemo(() => templates.filter(t => t.channel === "whatsapp"), [templates]);

  return (
    <div className="space-y-8" data-testid="messages-page">
      <div>
        <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Nachrichten & Vorlagen</h1>
        <p className="text-muted-foreground mt-1">Die 4 echten E-Mail-Templates (HTML · Resend) und 6 WhatsApp-Templates (Twilio) — jeweils auf Deutsch und Englisch.</p>
      </div>

      <TemplateSection title="E-Mail-Vorlagen" subtitle="HTML · Versand via Resend" icon={EnvelopeSimple} items={emailTemplates}
        onPreview={openPreview} onSend={(t)=>{ setSendFor(t); setSendBookingId(bookings[0]?.id || ""); }}
      />

      <TemplateSection title="WhatsApp-Vorlagen" subtitle="Text · Versand via Twilio" icon={WhatsappLogo} items={waTemplates}
        onPreview={openPreview} onSend={(t)=>{ setSendFor(t); setSendBookingId(bookings[0]?.id || ""); }}
      />

      <div className="msb-card" data-testid="sent-messages-log">
        <h3 className="text-lg font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Versendete Nachrichten (letzte 200)</h3>
        {messages.length === 0 && <div className="text-sm text-muted-foreground">Noch keine Nachrichten im Log.</div>}
        {messages.map(m => (
          <div key={m.id} className="py-2 border-b border-border last:border-0 flex justify-between text-sm">
            <div>
              <div className="font-medium">{m.subject}</div>
              <div className="text-xs text-muted-foreground">{m.type} → {m.recipient}</div>
            </div>
            <div className="text-right text-xs text-muted-foreground">{formatDate(m.sent_at)}<br/>{m.status}</div>
          </div>
        ))}
      </div>

      {/* Vorschau-Dialog */}
      <Dialog open={!!previewFor} onOpenChange={(o)=>!o && setPreviewFor(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewFor && (() => {
                const M = CHANNEL_META[previewFor.template.channel]; const Icon = M.icon;
                return <><Icon size={20} weight="fill"/> {previewFor.template.name} · {previewFor.lang.toUpperCase()}</>;
              })()}
            </DialogTitle>
          </DialogHeader>
          {previewFor && (
            <div className="space-y-3">
              {previewFor.template.channel === "email" && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Buchung für Vorschau:</span>
                  <Select value={previewBookingId} onValueChange={setPreviewBookingId}>
                    <SelectTrigger className="rounded-xl w-auto min-w-[280px]"><SelectValue placeholder="Buchung wählen"/></SelectTrigger>
                    <SelectContent>
                      {bookings.map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.guest?.first_name} {b.guest?.last_name} · {b.room?.name} · {formatDate(b.check_in)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {previewLoading && <div className="text-sm text-muted-foreground py-8 text-center">Lade Vorschau…</div>}

              {!previewLoading && previewData && previewFor.template.channel === "email" && (
                <div className="space-y-2">
                  <div className="text-xs text-muted-foreground">Betreff:</div>
                  <div className="text-sm font-medium p-2 bg-muted rounded-lg">{previewData.subject}</div>
                  <div className="text-xs text-muted-foreground mt-2">HTML-Vorschau:</div>
                  <iframe title="preview" srcDoc={previewData.html}
                    className="w-full h-[500px] border border-border rounded-lg bg-white"/>
                </div>
              )}

              {!previewLoading && previewData && previewFor.template.channel === "whatsapp" && (
                <div className="space-y-2">
                  {previewData.sample && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2 items-start">
                      <Info size={14} weight="fill" className="mt-0.5"/>
                      <span>Beispieldaten eingesetzt (Max · 08.08.2026 → 09.08.2026 · Doppelzimmer). Wähle beim Senden die richtige Buchung.</span>
                    </div>
                  )}
                  <pre className="whitespace-pre-wrap text-sm bg-green-50 border border-green-200 rounded-lg p-4 font-sans leading-relaxed max-h-[500px] overflow-y-auto">{previewData.body}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Senden-Dialog */}
      <Dialog open={!!sendFor} onOpenChange={(o)=>!o && setSendFor(null)}>
        <DialogContent data-testid="template-send-dialog">
          <DialogHeader>
            <DialogTitle>{sendFor && `${CHANNEL_META[sendFor.channel].label}: ${sendFor.name}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">Für welche Buchung soll die Nachricht jetzt gesendet werden? Die Sprache wird automatisch aus dem Gast (Buchungssprache) ermittelt.</div>
            <Select value={sendBookingId} onValueChange={setSendBookingId}>
              <SelectTrigger className="rounded-xl" data-testid="send-booking-picker"><SelectValue placeholder="Buchung wählen"/></SelectTrigger>
              <SelectContent>
                {bookings.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.guest?.first_name} {b.guest?.last_name} · {b.room?.name} · {formatDate(b.check_in)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={()=>setSendFor(null)} className="rounded-xl">Abbrechen</Button>
              <Button onClick={sendNow} disabled={sendBusy} className="rounded-xl bg-primary" data-testid="confirm-send-btn">
                <PaperPlaneTilt size={16} weight="bold"/><span className="ml-2">{sendBusy ? "Sende…" : "Jetzt senden"}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateSection({ title, subtitle, icon: SectionIcon, items, onPreview, onSend }) {
  return (
    <section className="space-y-3" data-testid={`section-${title.toLowerCase().replace(/[^a-z]/g,'-')}`}>
      <div className="flex items-center gap-2">
        <SectionIcon size={20} weight="fill" className="text-primary"/>
        <h2 className="text-xl font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>{title}</h2>
        <span className="text-sm text-muted-foreground">· {subtitle} · {items.length} Templates</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(t => (
          <TemplateCard key={`${t.channel}-${t.key}`} t={t} onPreview={onPreview} onSend={onSend}/>
        ))}
      </div>
    </section>
  );
}

function TemplateCard({ t, onPreview, onSend }) {
  const M = CHANNEL_META[t.channel];
  const Icon = M.icon;
  return (
    <div className="msb-card flex flex-col gap-3" data-testid={`tpl-${t.channel}-${t.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${M.accent}`}>
              <Icon size={12} weight="fill"/> {M.label}
            </span>
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">{t.key}</span>
          </div>
          <h3 className="text-lg font-semibold mt-1.5" style={{fontFamily:"'Work Sans',sans-serif"}}>{t.name}</h3>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
            <Clock size={12} weight="fill"/>
            <span>{t.when}</span>
          </div>
          <div className="text-[11px] text-muted-foreground/80 mt-1 font-mono">{t.trigger}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button size="sm" variant="outline" onClick={()=>onPreview(t, "de")} className="rounded-lg" data-testid={`preview-de-${t.channel}-${t.key}`}>
          <Eye size={14} weight="bold"/><span className="ml-1.5">Vorschau DE</span>
        </Button>
        <Button size="sm" variant="outline" onClick={()=>onPreview(t, "en")} className="rounded-lg" data-testid={`preview-en-${t.channel}-${t.key}`}>
          <Eye size={14} weight="bold"/><span className="ml-1.5">Preview EN</span>
        </Button>
        <Button size="sm" className="rounded-lg bg-primary ml-auto" onClick={()=>onSend(t)} data-testid={`send-${t.channel}-${t.key}`}>
          <PaperPlaneTilt size={14} weight="bold"/><span className="ml-1.5">Senden</span>
        </Button>
      </div>
    </div>
  );
}
